import { useState } from 'react'
import type { MouseEvent } from 'react'

/**
 * Shared header for the pre-gate pages (landing, /why). Self-contained, like
 * ErrorFallback.tsx: no imports beyond React, so it carries none of the risk
 * a heavier src/components/ dependency would add to a zero-backend render.
 * Owns its own sign-in-pending state so every page using it behaves
 * identically with zero props.
 */

const BACKEND = import.meta.env.VITE_API_URL ?? ''

const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent'

export default function PregateHeader() {
  const [signInPending, setSignInPending] = useState(false)

  const handleSignInClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (signInPending) {
      e.preventDefault()
      return
    }
    setSignInPending(true)
  }

  return (
    <header className="flex flex-col items-start justify-between gap-[18px] border-b border-brand-ink/[0.12] pb-[16px] min-[360px]:flex-row min-[360px]:gap-[14px] min-[768px]:gap-[24px] min-[768px]:pb-[20px]">
      <a
        href="/"
        className={`flex flex-col gap-[4px] text-brand-ink no-underline ${FOCUS_RING}`}
      >
        <span className="font-serif font-medium text-[18px] min-[768px]:text-[19px] tracking-[-0.01em]">
          Proof of Work
        </span>
        <span className="whitespace-nowrap text-[13.5px] min-[768px]:text-[13px] text-brand-slate tracking-[0.01em]">
          Your record of professional impact
        </span>
      </a>
      <a
        href={`${BACKEND}/auth/google`}
        onClick={handleSignInClick}
        aria-disabled={signInPending}
        className={`inline-flex flex-none items-center gap-2 whitespace-nowrap rounded-[3px] border border-brand-ink/45 bg-white px-[14px] py-[10px] text-[12.5px] font-medium text-brand-ink no-underline transition-colors hover:border-brand-ink hover:bg-brand-ink hover:text-brand-surface ${FOCUS_RING} ${
          signInPending ? 'pointer-events-none opacity-60' : ''
        }`}
      >
        {signInPending && (
          <span
            aria-hidden="true"
            className="inline-block h-[10px] w-[10px] animate-spin rounded-full border-[1.5px] border-brand-ink/25 border-t-brand-accent"
          />
        )}
        <span>{signInPending ? 'Signing in…' : 'Sign in'}</span>
      </a>
    </header>
  )
}
